/**
 * How much of the board an entity occupies, in world coordinates.
 *
 * This is what lets the renderer skip work. A board here runs to forty-two
 * thousand entities and most of them are off screen most of the time; four
 * comparisons against a cached box is far cheaper than asking the canvas to
 * draw something that lands outside it. The same box says how large the thing
 * would be on screen, which is what decides whether it is worth drawing at
 * all — a track a tenth of a pixel wide and text too small to read cost as
 * much to draw as the legible kind and tell the eye nothing.
 *
 * The boxes are generous rather than exact. A box slightly too large costs one
 * entity drawn that need not have been; a box too small loses geometry from
 * the screen, which is a bug people report as "my board has holes in it". So
 * every case here rounds outwards, and rotation is handled by transforming the
 * corners of the local box rather than trying to be clever.
 *
 * Kept apart from the renderer so that it can be measured on its own — a
 * change that claims to skip nine tenths of a board should be able to prove it
 * outside a browser.
 */

import type { Entity } from '@sircad/core/model.ts';
import { padOutline } from '@sircad/core/geom/pad.ts';
import { measure } from '@sircad/core/font/text.ts';

/** A world-space box. */
export interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The placement of a component's contents, as the renderer composes it. */
export interface Placement {
  tx: number;
  ty: number;
  cos: number;
  sin: number;
  mirror: number;
}

const IDENTITY: Placement = { tx: 0, ty: 0, cos: 1, sin: 0, mirror: 1 };

function place(x: number, y: number, m: Placement): [number, number] {
  const mx = x * m.mirror;
  return [m.tx + mx * m.cos - y * m.sin, m.ty + mx * m.sin + y * m.cos];
}

/** Grow a box to include a point. */
function add(b: Box, x: number, y: number): void {
  if (x < b.x1) b.x1 = x;
  if (x > b.x2) b.x2 = x;
  if (y < b.y1) b.y1 = y;
  if (y > b.y2) b.y2 = y;
}

const empty = (): Box => ({ x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity });

/**
 * The box an entity occupies once placed.
 *
 * Returns null for something with no extent to speak of, which the caller
 * should draw rather than skip — a missing box must never be read as "not on
 * screen".
 */
export function worldBounds(e: Entity, m: Placement = IDENTITY): Box | null {
  const b = empty();

  switch (e.kind) {
    case 'line': {
      const [ax, ay] = place(e.x1, e.y1, m);
      const [bx, by] = place(e.x2, e.y2, m);
      add(b, ax, ay);
      add(b, bx, by);
      // A bulged line bows out of the box its ends describe. The sagitta of an
      // arc is at most a quarter of its chord for the half-turn a bulge can
      // reach, so that much slack keeps the box honest without working out
      // where the bow actually goes.
      if (e.bulge) {
        const slack = Math.hypot(bx - ax, by - ay) / 4;
        b.x1 -= slack; b.y1 -= slack; b.x2 += slack; b.y2 += slack;
      }
      grow(b, e.width / 2);
      break;
    }

    case 'arc': {
      // The full circle, not the swept part. Working out which quadrants an
      // arc actually visits is a page of code to save a few pixels on the box.
      const [cx, cy] = place(e.cx, e.cy, m);
      const r = e.radius;
      add(b, cx - r, cy - r);
      add(b, cx + r, cy + r);
      grow(b, e.width / 2);
      break;
    }

    case 'spiral': {
      const [cx, cy] = place(e.cx, e.cy, m);
      const r = Math.max(e.r1, e.r2);
      add(b, cx - r, cy - r);
      add(b, cx + r, cy + r);
      grow(b, e.width / 2);
      break;
    }

    case 'pad': {
      // The pad's own outline, so a turned or shaped pad is boxed as it really
      // sits rather than as its two sizes suggest.
      for (const p of padOutline(e)) {
        const [x, y] = place(p.x, p.y, m);
        add(b, x, y);
      }
      break;
    }

    case 'fill': {
      for (const v of e.vertices) {
        const [x, y] = place(v.x, v.y, m);
        add(b, x, y);
      }
      grow(b, e.width / 2);
      break;
    }

    case 'text': {
      // `measure` returns em units, the same the layout works in, so the run's
      // world size is that times the cap height.
      const lines = (e.text ?? '').split(/[\r\nÿ]/);
      const em = Math.max(0, ...lines.map(measure));
      const w = em * e.height * (e.aspect && e.aspect > 0 ? e.aspect : 1);
      const h = (lines.length === 1 ? 1 : (lines.length - 1) * 1.5 + 1) * e.height;
      const [x, y] = place(e.x, e.y, m);
      // The anchor decides which way the run extends and a turned string
      // sweeps a circle of its own length, so the diagonal about the insertion
      // point covers every case without unpicking the anchor grid here. It is
      // generous, which is the safe direction for a box.
      const reach = Math.hypot(w, h);
      add(b, x - reach, y - reach);
      add(b, x + reach, y + reach);
      break;
    }

    case 'component': {
      // A component's own box is the union of its children's, which the caller
      // has usually already flattened; this is for the rare unflattened case.
      const inner = compose(m, e.x, e.y, e.rotation ?? 0, e.mirrored === true);
      for (const c of e.children) {
        const cb = worldBounds(c, inner);
        if (!cb) continue;
        add(b, cb.x1, cb.y1);
        add(b, cb.x2, cb.y2);
      }
      break;
    }

    default:
      return null;
  }

  return Number.isFinite(b.x1) ? b : null;
}

function grow(b: Box, by: number): void {
  if (!(by > 0)) return;
  b.x1 -= by; b.y1 -= by; b.x2 += by; b.y2 += by;
}

/** The placement a component gives its children, matching the renderer's. */
export function compose(
  parent: Placement, x: number, y: number, rotation: number, mirrored: boolean,
): Placement {
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const mirror = mirrored ? -1 : 1;
  const [ox, oy] = place(x, y, parent);
  return {
    tx: ox,
    ty: oy,
    cos: parent.cos * cos - parent.sin * sin,
    sin: parent.sin * cos + parent.cos * sin,
    mirror: parent.mirror * mirror,
  };
}

/** Whether two boxes overlap at all. */
export function overlaps(a: Box, b: Box): boolean {
  return a.x1 <= b.x2 && a.x2 >= b.x1 && a.y1 <= b.y2 && a.y2 >= b.y1;
}
